import * as React from 'react';
import { PerformanceMetric, Property } from '../../constants/telemetry';
import { DisplayState } from '../../redux/selector/display/display-state';
import { trackMetric } from '../../utilities/telemetry/channel';

export type HomeResourceCountBucket = '0' | '1' | '2-5' | '6-20' | '21+';

interface HomeLoadPerformanceSnapshot {
    devBoxCount: number;
    displayState: DisplayState;
    environmentCount: number;
}

interface HomeInitialLoadSnapshot {
    snapshot: HomeLoadPerformanceSnapshot;
}

export interface HomeLoadPerformanceTelemetryProps {
    devBoxCount: number;
    displayState: DisplayState;
    environmentCount: number;
    isHomeContentRendered: boolean;
}

export const getHomeResourceCountBucket = (count: number): HomeResourceCountBucket => {
    if (count === 0) {
        return '0';
    }

    if (count === 1) {
        return '1';
    }

    if (count <= 5) {
        return '2-5';
    }

    return count <= 20 ? '6-20' : '21+';
};

const getNavigationType = (): string => {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return navigationEntry?.type ?? 'unknown';
};

export const createHomeLoadPerformanceMetric = (
    metric: PerformanceMetric,
    snapshot: HomeLoadPerformanceSnapshot
): { duration: number; metric: PerformanceMetric; options: Parameters<typeof trackMetric>[2] } => ({
    duration: performance.now(),
    metric,
    options: {
        properties: {
            [Property.DevBoxCountBucket]: getHomeResourceCountBucket(snapshot.devBoxCount),
            [Property.HomeDisplayState]: snapshot.displayState,
            [Property.EnvironmentCountBucket]: getHomeResourceCountBucket(snapshot.environmentCount),
            [Property.NavigationType]: getNavigationType(),
        },
    },
});

let activeMountCount = 0;
let hasAbandonedDocumentLoad = false;
let hasStartedDocumentLoad = false;
let initialLoadSnapshot: HomeInitialLoadSnapshot | undefined;
let trackedNavigationStart: number | undefined;
const trackedMetrics = new Set<PerformanceMetric>();

export const homeLoadPerformanceTelemetryChannel = { trackMetric };

export const homeLoadPerformanceTelemetry = {
    captureInitialLoad: (
        devBoxCount: number,
        environmentCount: number,
        displayState: DisplayState
    ): HomeInitialLoadSnapshot | undefined => {
        if (hasAbandonedDocumentLoad) {
            return undefined;
        }

        if (!initialLoadSnapshot) {
            initialLoadSnapshot = {
                snapshot: {
                    devBoxCount,
                    displayState,
                    environmentCount,
                },
            };
        }

        return initialLoadSnapshot;
    },
    emit: (metric: PerformanceMetric, snapshot: HomeLoadPerformanceSnapshot): void => {
        const measurement = createHomeLoadPerformanceMetric(metric, snapshot);
        homeLoadPerformanceTelemetryChannel.trackMetric(measurement.metric, measurement.duration, measurement.options);
    },
    trackOnce: (metric: PerformanceMetric, snapshot: HomeLoadPerformanceSnapshot): void => {
        if (activeMountCount === 0 || hasAbandonedDocumentLoad || trackedMetrics.has(metric)) {
            return;
        }

        trackedMetrics.add(metric);
        homeLoadPerformanceTelemetry.emit(metric, snapshot);
    },
    startDocumentLoad: (): boolean => {
        const navigationStart = performance.timeOrigin;

        if (navigationStart !== trackedNavigationStart) {
            homeLoadPerformanceTelemetry.resetDocumentLoad(navigationStart);
        }

        activeMountCount += 1;

        if (hasAbandonedDocumentLoad || hasStartedDocumentLoad) {
            return false;
        }

        hasStartedDocumentLoad = true;
        return true;
    },
    endDocumentLoad: (): void => {
        activeMountCount = Math.max(0, activeMountCount - 1);

        Promise.resolve().then(() => {
            if (activeMountCount === 0 && !initialLoadSnapshot) {
                hasAbandonedDocumentLoad = true;
            }
        });
    },
    getInitialLoad: (): HomeInitialLoadSnapshot | undefined => initialLoadSnapshot,
    resetDocumentLoad: (navigationStart: number): void => {
        activeMountCount = 0;
        hasAbandonedDocumentLoad = false;
        hasStartedDocumentLoad = false;
        initialLoadSnapshot = undefined;
        trackedMetrics.clear();
        trackedNavigationStart = navigationStart;
    },
};

export const HomeLoadPerformanceTelemetry: React.FC<HomeLoadPerformanceTelemetryProps> = (
    props: HomeLoadPerformanceTelemetryProps
) => {
    const { devBoxCount, displayState, environmentCount, isHomeContentRendered } = props;

    React.useEffect(() => {
        homeLoadPerformanceTelemetry.startDocumentLoad();

        return () => homeLoadPerformanceTelemetry.endDocumentLoad();
        // This effect defines the lifetime of one document-scoped load and must not restart as resource counts change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!isHomeContentRendered || homeLoadPerformanceTelemetry.getInitialLoad()) {
            return;
        }

        const initialLoad = homeLoadPerformanceTelemetry.captureInitialLoad(
            devBoxCount,
            environmentCount,
            displayState
        );

        if (!initialLoad) {
            return;
        }

        const { snapshot } = initialLoad;

        homeLoadPerformanceTelemetry.trackOnce(PerformanceMetric.HomeContentRendered, snapshot);
    }, [devBoxCount, displayState, environmentCount, isHomeContentRendered]);

    return null;
};
